// The record of a voice conversation: who said what, and how much of it was actually heard.
//
// Pure — no timers, no DOM, no Web Audio. The one thing here worth being sure of is the
// arithmetic that decides how much of an answer has been spoken aloud, and it is only worth being
// sure of if a test can reach it. voiceSession.js drives this; Voice.js renders what it returns.

/**
 * How many characters of an answer had been said aloud by [playedMs].
 *
 * [marks] pair a position in the answer's audio with the length of the transcript that had arrived
 * when that audio was booked, so the count is measured against the speakers rather than guessed
 * from the clock. Past the last mark the answer stops there: text whose audio has not been booked
 * yet has not been said, however much of it the provider has already written.
 */
export function spokenLength(marks, playedMs) {
  let previous = { ms: 0, length: 0 };
  for (const mark of marks) {
    if (playedMs < mark.ms) {
      const span = mark.ms - previous.ms;
      const grown = mark.length - previous.length;
      return Math.floor(previous.length + ((playedMs - previous.ms) / span) * grown);
    }
    previous = mark;
  }
  return previous.length;
}

export function createVoiceTranscript() {
  let turns = [];
  let nextId = 0;

  function add(turn) {
    const created = {
      id: nextId,
      buffer: '',
      marks: [],
      played: 0,
      complete: false,
      lost: false,
      ...turn,
    };
    nextId += 1;
    turns = [...turns, created];
    return created;
  }

  // The answer these events belong to: the most recent one, which is not the most recent turn. An
  // interruption commits the student's turn while the provider is still delivering the answer it
  // silenced, so the last bubble in the record is theirs and the tail is not.
  function answering() {
    return [...turns].reverse().find((turn) => turn.speaker === 'ai');
  }

  /** A new conversation. The one before it is not this one's past, whatever the screen showed. */
  function cleared() {
    turns = [];
  }

  function answerStarted() {
    add({ speaker: 'ai' });
  }

  /** A piece of the answer's transcript, which the provider writes ahead of saying it. */
  function spoke(text) {
    const turn = answering();
    if (turn) {
      turn.buffer += text;
    }
  }

  /**
   * Audio has been booked to play up to [ms] into the answer, carrying the transcript so far.
   *
   * A booking that does not reach past the last one is the speakers' count starting again rather
   * than a later position in this answer, and is dropped: taken at face value it would put the
   * marks out of order, and the reveal reads them as ascending. Everything downstream of that
   * assumption — including never showing a word before its audio — rests on it holding here.
   */
  function booked(ms) {
    const turn = answering();
    if (turn && ms > (turn.marks.at(-1)?.ms ?? 0)) {
      turn.marks = [...turn.marks, { ms, length: turn.buffer.length }];
    }
  }

  /**
   * The speakers have reached [ms] into the answer.
   *
   * Kept as a high-water mark, never overwritten. Called on a timer for as long as the session
   * lives, so most of the time there is no answer to measure — before the first one, and in every
   * silence between them — and an emptied queue reports nothing played. That is a fact about the
   * queue rather than about the answer: words already said stay said, and taking the zero at face
   * value would wind the answer back to its first character every time the student pressed to
   * speak.
   */
  function playedTo(ms) {
    const turn = answering();
    if (turn) {
      turn.played = Math.max(turn.played, ms);
    }
  }

  /**
   * Nothing more of this answer is coming. Once the speakers have played everything booked for it,
   * whatever the transcript wrote after the last chunk was said inside that chunk: the words are
   * the audio's, not a further one's, and holding them back would show an answer heard in full as
   * an answer cut off.
   */
  function answerEnded() {
    const turn = answering();
    if (turn) {
      turn.complete = true;
    }
  }

  /**
   * The student cut the answer off after [heardMs] of it, reporting where in its words that was.
   *
   * The measurement is the speakers' own, the same one that goes upstream as `audio_end_ms`, so
   * the record and the model's memory of the answer are cut at one point rather than at two that
   * agree by luck. The offset it returns is that same cut counted in characters, which is what the
   * server keeps a scene conversation with.
   */
  function interrupted(heardMs) {
    const turn = answering();
    if (!turn) {
      return null;
    }
    turn.cutAt = spokenLength(turn.marks, heardMs);
    return turn.cutAt;
  }

  /** The student's turn, made when it is committed and filled in when it comes back transcribed. */
  function committed() {
    add({ speaker: 'student', itemId: null });
  }

  /**
   * Name the turn just committed, which the server does once it has taken it. Until then there is
   * nothing to match a transcription against — the browser makes the bubble, the server names it.
   */
  function bound(itemId) {
    const unnamed = waiting();
    if (unnamed) {
      unnamed.itemId = itemId;
    }
  }

  /**
   * The oldest committed turn the server has not named yet.
   *
   * Oldest, because the server names turns in the order it took them, and a second can be
   * committed before the first is named. Taking the newest would cross the two, and the student
   * would read their own sentences swapped.
   */
  function waiting() {
    return turns.find((it) => it.speaker === 'student' && !it.itemId);
  }

  /**
   * The server refused the commit, so no item was ever made and the error names none. The turn
   * happened all the same: the student held the button and spoke into it.
   */
  function commitRejected() {
    const unnamed = waiting();
    if (unnamed) {
      unnamed.lost = true;
    }
  }

  /**
   * The turn the server named [itemId], if there is one.
   *
   * An event carrying no name matches nothing. Only the student's turns are ever named, so a
   * search for an absent id would otherwise find the AI's — which carry none — and rewrite the
   * answer on screen.
   */
  function named(itemId) {
    return itemId ? turns.find((it) => it.itemId === itemId) : undefined;
  }

  /**
   * What the student turned out to have said. Nothing at all is a loss, not an empty turn: the
   * button was held and something went up the wire, and a bubble left pending would wait for a
   * text that has already come and gone.
   */
  function transcribed(itemId, text) {
    const turn = named(itemId);
    if (turn) {
      turn.buffer = text.trim();
      turn.lost = turn.buffer === '';
    }
  }

  /** The turn was spoken and will never have words: transcription refused it, or heard nothing. */
  function failed(itemId) {
    const turn = named(itemId);
    if (turn) {
      turn.lost = true;
    }
  }

  // The student's own words are not paced: they were heard in the room as they were said, and
  // there is nothing left to wait for. Only the AI's are metered out against its speakers.
  //
  // [answer] is the answer being spoken now, which is the only one still being measured: the
  // speakers report their position in that one alone, so every earlier answer's count stopped
  // wherever it had reached when this one began.
  function shownAs(turn, answer) {
    if (turn.speaker === 'student') {
      return {
        id: turn.id,
        speaker: turn.speaker,
        text: turn.buffer,
        struck: '',
        pending: turn.buffer === '' && !turn.lost,
        lost: turn.lost,
      };
    }

    // An answer another one has followed is finished with, whatever the speakers still have of it
    // queued: it plays on to its end behind the new one, and the only thing that could have cut it
    // short sets `cutAt` when it does. Its own count stopped the moment the next answer became the
    // one being measured, so left paced it would stand in the record broken off at whatever word
    // was being said then — which is a character interrupting itself, once per thing it does.
    const overtaken = turn.complete && turn !== answer;
    // Everything booked for it has been played, and there is no more to come: the words written
    // after the last chunk were said inside it.
    const heardItAll =
      turn.complete &&
      turn.marks.length > 0 &&
      (overtaken || turn.played >= turn.marks.at(-1).ms);
    // It ended without a chunk of it ever being booked — cancelled before the first one arrived,
    // or failed upstream. Nothing of it was ever going to be heard, so it is cut off at nothing.
    const silent = turn.complete && turn.marks.length === 0;
    const cutAt = turn.cutAt ?? (silent ? 0 : null);
    const spokenSoFar =
      cutAt ?? (heardItAll ? turn.buffer.length : spokenLength(turn.marks, turn.played));
    return {
      id: turn.id,
      speaker: turn.speaker,
      text: turn.buffer.slice(0, spokenSoFar),
      struck: cutAt == null ? '' : turn.buffer.slice(spokenSoFar),
      pending: spokenSoFar === 0 && cutAt == null,
      // An answer that ended with nothing written and nothing said is as gone as a turn that came
      // back untranscribable, and is marked the same way rather than left waiting for words that
      // have already not arrived.
      lost: turn.lost || (silent && turn.buffer === ''),
    };
  }

  return {
    cleared,
    answerStarted,
    spoke,
    booked,
    playedTo,
    answerEnded,
    interrupted,
    committed,
    bound,
    transcribed,
    failed,
    commitRejected,
    turns: () => {
      const answer = answering();
      return turns.map((turn) => shownAs(turn, answer));
    },
  };
}
